import React from "react";
import {
    Box,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";

/**
 * MarketingPhrasesRowV2
 *
 * Renders: [icons x N] phrase1 [icons x N] phrase2 ... [icons x N]
 *
 * Props:
 * - phrases: array of strings or ReactNodes
 * - iconSrc: image url/path for the icon (optional if iconNode is provided)
 * - iconNode: ReactNode icon (optional if iconSrc is provided)
 * - iconRepeat: number of times to repeat the icon group (default: 2)
 * - iconSize: number|string (px) for icon width/height (default: 18)
 * - gap: spacing between items (default: 1.25)
 * - iconGap: spacing between icons inside a group (default: 0.5)
 * - dividerGap: spacing between icon group and phrase text (default: 1)
 * - textSx: sx overrides applied to Typography
 * - containerSx: sx overrides applied to container Stack
 */
export default function MarketingPhrasesRowV2({
                                                  phrases = [],
                                                  iconSrc,
                                                  iconNode,
                                                  iconRepeat = 2,
                                                  iconSize,
                                                  gap,
                                                  iconGap = 0.5,
                                                  dividerGap = 1,
                                                  color,
                                                  containerSx,
                                                  rotateIcon = false,
                                              }) {
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

    const safePhrases = Array.isArray(phrases) ? phrases.filter(Boolean) : [];
    const repeat = Math.max(0, Number(iconRepeat) || 0);

    // ✅ Only responsive change
    const effectiveRepeat = isSmall ? Math.max(0, repeat - 1) : repeat;

    const Icon = ({ index }) => {
        if (iconNode) {
            return (
                <Box key={index} sx={{ display: "inline-flex" }}>
                    {iconNode}
                </Box>
            );
        }

        if (!iconSrc) return null;

        return (
            <Box
                key={index}
                component="img"
                src={iconSrc}
                alt=""
                sx={{
                    width: iconSize,
                    height: iconSize,
                    display: "block",
                    objectFit: "contain",
                    transform: rotateIcon ? "rotate(90deg)" : "none",
                }}
            />
        );
    };

    const IconGroup = ({ keyName }) => {
        if (effectiveRepeat === 0) return null;

        return (
            <Stack
                key={keyName}
                direction="row"
                alignItems="center"
                spacing={iconGap}
                sx={{ flex: "0 0 auto" }}
            >
                {Array.from({ length: effectiveRepeat }).map((_, i) => (
                    <Icon key={i} index={i} />
                ))}
            </Stack>
        );
    };

    if (safePhrases.length === 0) return null;

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={gap}
            sx={{
                width: "100%",
                flexWrap: "wrap",
                ...containerSx,
            }}
        >
            {/* Leading icons */}
            <IconGroup keyName="leading-icons" />

            {safePhrases.map((phrase, idx) => (
                <React.Fragment key={idx}>
                    <Typography
                        sx={{
                            fontFamily: `"Georgia", "Times New Roman", serif`,
                            fontWeight: 300,
                            letterSpacing: 1,
                            color: color,
                            fontSize: { xs: 12, sm: 18, md: 20, lg: 24 },
                            textAlign: "center",
                        }}
                    >
                        {phrase}
                    </Typography>

                    <Box sx={{ mx: dividerGap / 2 }}>
                        <IconGroup keyName={`icons-after-${idx}`} />
                    </Box>
                </React.Fragment>
            ))}
        </Stack>
    );
}
